import { useState, useEffect, useCallback } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import {
  Search, Plus, X, Truck, Loader2, Pencil, Trash2, Save, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { api } from '@/utils/api';
import { VendorProductsDrawer } from './vendorProducts/VendorProductsDrawer';
import { VendorProductsExcelButtons } from './vendorProducts/VendorProductsExcel';
import { gstVendorName, legalNameUnder } from './gstVendorName';

interface VendorAddress {
  line1?: string;
  city?: string;
  state?: string;
  pincode?: string;
}

interface Vendor {
  id: string;
  name: string;
  legalName?: string | null;
  contactPerson?: string;
  phone?: string;
  email?: string;
  gstin?: string;
  pan?: string;
  stateCode?: string;
  paymentTerms?: string;
  isActive?: boolean;
  productCount?: number;
  billingAddress?: VendorAddress;
  deliveryAddress?: VendorAddress;
}

const PAYMENT_TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60'];

const GST_OFFLINE_MSG = 'GST registry not reachable — only PAN and state filled';

type AddrForm = { line1: string; city: string; state: string; pincode: string };

/**
 * What a GST lookup fills. Registry answer (source 'api'): Vendor Name = trade name when GST has
 * one, else legal name (SUN-239; the legal name shows on its own line), billing address from the
 * registered address.
 * Offline answer: PAN, state code and billing state only.
 */
function gstFill<T extends { name: string; pan: string; stateCode: string; billingAddress: AddrForm }>(d: any, prev: T) {
  const fromRegistry = d?.source === 'api';
  const ba = fromRegistry ? d.billingAddress : null;
  const line1 = ba?.line1
    ? [ba.line1, ba.line2].filter(Boolean).join(', ')
    : (fromRegistry && d.address) || prev.billingAddress.line1;
  return {
    name: (fromRegistry && gstVendorName(d).name) || prev.name,
    pan: d?.pan || prev.pan,
    stateCode: d?.stateCode || prev.stateCode,
    billingAddress: {
      ...prev.billingAddress,
      line1,
      city: ba?.city || prev.billingAddress.city,
      state: ba?.state || d?.stateName || prev.billingAddress.state,
      pincode: ba?.pincode || prev.billingAddress.pincode,
    },
  };
}

export function VendorsPage() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // SUN-174: products this vendor supplies
  const [productsFor, setProductsFor] = useState<Vendor | null>(null);
  // SUN-180: bumped after an Excel save so the drawer reloads its list
  const [drawerRound, setDrawerRound] = useState(0);

  // Add Vendor Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasGst, setHasGst] = useState<boolean | null>(null);
  const [gstFetching, setGstFetching] = useState(false);
  const [gstFetched, setGstFetched] = useState(false);
  const [gstError, setGstError] = useState('');
  const [gstEnrichment, setGstEnrichment] = useState<any>(null);
  // 'api' only when the GST registry really answered; 'offline' = PAN/state parsed from the GSTIN string.
  const [gstSource, setGstSource] = useState<'api' | 'offline' | null>(null);
  const [gstApiError, setGstApiError] = useState('');
  const [form, setForm] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    gstin: '',
    pan: '',
    stateCode: '',
    paymentTerms: 'Net 30',
    billingAddress: { line1: '', city: '', state: '', pincode: '' },
    deliveryAddress: { line1: '', city: '', state: '', pincode: '' },
    sameAsB: false,
  });

  // Edit Vendor Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '', name: '', contactPerson: '', phone: '', email: '', gstin: '', pan: '', stateCode: '', paymentTerms: 'Net 30',
    billingAddress: { line1: '', city: '', state: '', pincode: '' },
    deliveryAddress: { line1: '', city: '', state: '', pincode: '' },
    sameAsB: false,
  });

  const [editOriginalGstin, setEditOriginalGstin] = useState('');
  const [editSavedLegal, setEditSavedLegal] = useState<string | null>(null);
  const [editEnrichment, setEditEnrichment] = useState<any>(null);
  const [editGstError, setEditGstError] = useState('');
  const [editGstFetching, setEditGstFetching] = useState(false);
  const [editGstSource, setEditGstSource] = useState<'api' | 'offline' | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      params.page = String(page);
      params.limit = String(limit);
      if (showInactive) params.isActive = 'all';
      const res = await api.get('/vendors', { params });
      setVendors(res.data.data?.vendors ?? res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? res.data.data?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [search, page, showInactive]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // GST lookup for vendors
  const handleGstLookup = async (gstin: string) => {
    if (gstin.length !== 15) return;
    setGstFetching(true);
    setGstError('');
    setGstFetched(false);
    setGstEnrichment(null);
    setGstSource(null);
    setGstApiError('');
    try {
      const res = await api.get(`/vendors/gst-lookup/${gstin}`);
      const d = res.data.data;
      const fromRegistry = d.source === 'api';
      setForm(prev => {
        const next = { ...prev, ...gstFill(d, prev) };
        return prev.sameAsB ? { ...next, deliveryAddress: { ...next.billingAddress } } : next;
      });

      // AI Address Parser — only when the registry gave a one-line address and no parts (1 credit)
      if (fromRegistry && d.address && !d.billingAddress?.line1) {
        try {
          const addrRes = await api.post('/ai/parse-address', { rawAddress: d.address, stateName: d.stateName || '' });
          const ap = addrRes.data.data;
          if (ap?.parsed) {
            setForm(prev => ({
              ...prev,
              billingAddress: {
                line1: ap.line1 || prev.billingAddress.line1,
                city: ap.city || prev.billingAddress.city,
                state: ap.state || prev.billingAddress.state,
                pincode: ap.pincode || prev.billingAddress.pincode,
              },
            }));
          }
        } catch { /* AI parse failed — raw address stays in line1, which is fine */ }
      }
      if (fromRegistry) {
        setGstEnrichment({
          tradeName: d.tradeName || null,
          legalName: d.legalName || null,
          gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null,
          gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null,
          gstRawData: d.rawData || null,
        });
      }
      setGstSource(fromRegistry ? 'api' : 'offline');
      setGstApiError(fromRegistry ? '' : (d.apiError || ''));
      setGstFetched(true);
    } catch (err: any) {
      setGstError(err.response?.data?.error || 'Failed to fetch GST details');
    } finally {
      setGstFetching(false);
    }
  };

  // Editing the GSTIN re-runs the GST lookup so state, PAN and GST status follow the new number.
  // "Fill from GST" re-runs the same lookup for an unchanged GSTIN (old vendors saved before the fill worked).
  const handleEditGstinChange = async (value: string, force = false) => {
    const v = value.trim().toUpperCase();
    setEditForm(prev => ({ ...prev, gstin: v }));
    setEditEnrichment(null);
    setEditGstError('');
    setEditGstSource(null);
    if (v.length !== 15 || (!force && v === editOriginalGstin)) return;
    setEditGstFetching(true);
    try {
      const d = (await api.get(`/vendors/gst-lookup/${v}`)).data.data;
      setEditForm(prev => {
        const next = { ...prev, ...gstFill(d, prev) };
        return prev.sameAsB ? { ...next, deliveryAddress: { ...next.billingAddress } } : next;
      });
      setEditGstSource(d.source === 'api' ? 'api' : 'offline');
      if (d.source === 'api') {
        setEditEnrichment({
          tradeName: d.tradeName || null, legalName: d.legalName || null, gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null, gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null, gstRawData: d.rawData || null,
        });
      }
      if (d.source !== 'api') setEditGstError(GST_OFFLINE_MSG);
    } catch (err: any) {
      setEditGstError(err.response?.data?.error || 'Failed to fetch GST details');
    } finally {
      setEditGstFetching(false);
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await api.put(`/vendors/${id}`, { isActive: true });
      fetchVendors();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reactivate vendor');
    }
  };

  const openCreateModal = () => {
    setHasGst(null);
    setGstFetched(false);
    setGstError('');
    setGstEnrichment(null);
    setGstSource(null);
    setGstApiError('');
    setForm({ name: '', contactPerson: '', phone: '', email: '', gstin: '', pan: '', stateCode: '', paymentTerms: 'Net 30', billingAddress: { line1: '', city: '', state: '', pincode: '' }, deliveryAddress: { line1: '', city: '', state: '', pincode: '' }, sameAsB: false });
    setShowModal(true);
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      const billingAddr = form.billingAddress.line1 ? form.billingAddress : undefined;
      const deliveryAddr = form.sameAsB ? billingAddr : (form.deliveryAddress.line1 ? form.deliveryAddress : undefined);
      await api.post('/vendors', {
        name: form.name,
        contactPerson: form.contactPerson || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        gstin: form.gstin || undefined,
        pan: form.pan || undefined,
        stateCode: form.stateCode || undefined,
        paymentTerms: form.paymentTerms,
        billingAddress: billingAddr,
        deliveryAddress: deliveryAddr,
        // GST enrichment fields from API lookup
        ...(gstEnrichment || {}),
      });
      setShowModal(false);
      setForm({ name: '', contactPerson: '', phone: '', email: '', gstin: '', pan: '', stateCode: '', paymentTerms: 'Net 30', billingAddress: { line1: '', city: '', state: '', pincode: '' }, deliveryAddress: { line1: '', city: '', state: '', pincode: '' }, sameAsB: false });
      fetchVendors();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to deactivate this vendor?')) return;
    try {
      await api.delete(`/vendors/${id}`);
      fetchVendors();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete vendor');
    }
  };

  const openEditModal = (v: Vendor) => {
    const ba = v.billingAddress || { line1: '', city: '', state: '', pincode: '' };
    const da = v.deliveryAddress || { line1: '', city: '', state: '', pincode: '' };
    const same = !!(ba.line1 && da.line1 && ba.line1 === da.line1 && ba.city === da.city && ba.state === da.state && ba.pincode === da.pincode);
    setEditForm({
      id: v.id,
      name: v.name || '',
      contactPerson: v.contactPerson || '',
      phone: v.phone || '',
      email: v.email || '',
      gstin: v.gstin || '',
      pan: v.pan || '',
      stateCode: v.stateCode || '',
      paymentTerms: v.paymentTerms || 'Net 30',
      billingAddress: { line1: ba.line1 || '', city: ba.city || '', state: ba.state || '', pincode: ba.pincode || '' },
      deliveryAddress: { line1: da.line1 || '', city: da.city || '', state: da.state || '', pincode: da.pincode || '' },
      sameAsB: same,
    });
    setEditOriginalGstin((v.gstin || '').toUpperCase());
    setEditSavedLegal(v.legalName || null);
    setEditEnrichment(null);
    setEditGstError('');
    setEditGstSource(null);
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    try {
      setEditSaving(true);
      const billingAddr = editForm.billingAddress.line1 ? editForm.billingAddress : undefined;
      const deliveryAddr = editForm.sameAsB ? billingAddr : (editForm.deliveryAddress.line1 ? editForm.deliveryAddress : undefined);
      await api.put(`/vendors/${editForm.id}`, {
        name: editForm.name,
        contactPerson: editForm.contactPerson || undefined,
        phone: editForm.phone || undefined,
        email: editForm.email || undefined,
        gstin: editForm.gstin || undefined,
        pan: editForm.pan || undefined,
        stateCode: editForm.stateCode || undefined,
        paymentTerms: editForm.paymentTerms,
        billingAddress: billingAddr,
        deliveryAddress: deliveryAddr,
        ...(editEnrichment || {}),
      });
      setShowEditModal(false);
      fetchVendors();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update vendor');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="text-sm text-gray-500">Manage your supplier and vendor directory</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
        >
          <Plus size={16} />
          Add Vendor
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search vendors..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <label className="mt-2 flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }} />
          Show deactivated vendors
        </label>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading vendors...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchVendors} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : vendors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Truck size={40} className="mb-2" />
            <p className="text-sm">No vendors found</p>
            <p className="text-xs">Add your first vendor to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Contact Person</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">GSTIN</th>
                  <th className="px-4 py-3">Billing Address</th>
                  <th className="px-4 py-3">Delivery Address</th>
                  <th className="px-4 py-3">Payment Terms</th>
                  <th className="px-4 py-3">Products</th>
                  <th className="px-4 py-3">Active</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vendors.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {v.name}
                      {legalNameUnder(v.name, v.legalName) && <div className="text-xs font-normal text-gray-500">{legalNameUnder(v.name, v.legalName)}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{v.contactPerson || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{v.phone || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{v.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{v.gstin || '-'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {v.billingAddress?.line1 ? (
                        <span>{v.billingAddress.line1}{v.billingAddress.city ? `, ${v.billingAddress.city}` : ''}{v.billingAddress.pincode ? ` - ${v.billingAddress.pincode}` : ''}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {v.deliveryAddress?.line1 ? (
                        v.billingAddress?.line1 === v.deliveryAddress.line1 && v.billingAddress?.city === v.deliveryAddress.city
                          ? <span className="text-gray-400 italic">Same as billing</span>
                          : <span>{v.deliveryAddress.line1}{v.deliveryAddress.city ? `, ${v.deliveryAddress.city}` : ''}{v.deliveryAddress.pincode ? ` - ${v.deliveryAddress.pincode}` : ''}</span>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{v.paymentTerms || '-'}</td>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setProductsFor(v)} className="font-medium text-blue-700 hover:underline">
                        {v.productCount ?? 0} {v.productCount === 1 ? 'product' : 'products'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {v.isActive !== false ? (
                        <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">Active</span>
                      ) : (
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setProductsFor(v); }} className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">⛓ Products</button>
                        <button onClick={(e) => { e.stopPropagation(); openEditModal(v); }} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                        {v.isActive === false
                          ? <button onClick={(e) => { e.stopPropagation(); handleReactivate(v.id); }} className="rounded px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50" title="Reactivate">Reactivate</button>
                          : <button onClick={(e) => { e.stopPropagation(); handleDelete(v.id); }} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Deactivate"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
          </div>
        </div>
      )}

      {productsFor && (
        <VendorProductsDrawer
          vendor={productsFor}
          onClose={() => setProductsFor(null)}
          onChanged={fetchVendors}
          reloadToken={drawerRound}
          headerActions={<VendorProductsExcelButtons onSaved={() => { setDrawerRound((n) => n + 1); fetchVendors(); }} />}
        />
      )}

      {/* Add Vendor Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Vendor</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              {/* Step 1: GST Yes/No */}
              {hasGst === null && (
                <div className="flex flex-col items-center gap-4 py-6">
                  <p className="text-sm font-medium text-gray-700">Does this vendor have a GSTIN?</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setHasGst(true)}
                      className="flex items-center gap-2 rounded-lg border-2 border-green-200 bg-green-50 px-6 py-3 text-sm font-medium text-green-700 hover:border-green-400 hover:bg-green-100 transition-colors"
                    >
                      <CheckCircle2 size={18} />
                      Yes, has GST
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasGst(false)}
                      className="flex items-center gap-2 rounded-lg border-2 border-gray-200 bg-gray-50 px-6 py-3 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-gray-100 transition-colors"
                    >
                      <X size={18} />
                      No GST
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2a: GSTIN input + auto-fetch */}
              {hasGst === true && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">GSTIN *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={form.gstin}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
                          setForm({ ...form, gstin: v });
                          setGstFetched(false);
                          setGstError('');
                          if (v.length === 15) handleGstLookup(v);
                        }}
                        placeholder="Enter 15-character GSTIN"
                        maxLength={15}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono tracking-wider focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                        autoFocus
                      />
                      {gstFetching && <Loader2 size={20} className="animate-spin text-[var(--saffron-600)] mt-2" />}
                      {gstFetched && gstSource === 'api' && <CheckCircle2 size={20} className="text-green-500 mt-2" />}
                    </div>
                    {gstError && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                        <AlertCircle size={12} />
                        {gstError}
                      </p>
                    )}
                    {gstFetched && (
                      <div className="mt-2 space-y-1">
                        {gstSource === 'api' ? (
                          <p className="text-xs text-green-600">Details fetched — review and edit below if needed</p>
                        ) : (
                          <p className="flex items-center gap-1 text-xs text-orange-600" title={gstApiError || undefined}>
                            <AlertCircle size={12} />
                            {GST_OFFLINE_MSG}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {gstEnrichment?.gstStatus && (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              gstEnrichment.gstStatus === 'Active' ? 'bg-green-100 text-green-800' :
                              gstEnrichment.gstStatus === 'Cancelled' ? 'bg-red-100 text-red-800' :
                              gstEnrichment.gstStatus === 'Suspended' ? 'bg-orange-100 text-orange-800' :
                              gstEnrichment.gstStatus === 'Inactive' ? 'bg-gray-100 text-gray-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${
                                gstEnrichment.gstStatus === 'Active' ? 'bg-green-500' :
                                gstEnrichment.gstStatus === 'Cancelled' ? 'bg-red-500' :
                                gstEnrichment.gstStatus === 'Suspended' ? 'bg-orange-500' :
                                gstEnrichment.gstStatus === 'Inactive' ? 'bg-gray-500' :
                                'bg-yellow-500'
                              }`} />
                              GST: {gstEnrichment.gstStatus}
                            </span>
                          )}
                          {gstEnrichment?.gstBusinessType && (
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                              {gstEnrichment.gstBusinessType}
                            </span>
                          )}
                          {gstEnrichment?.gstRegistrationDate && (
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                              Reg: {gstEnrichment.gstRegistrationDate}
                            </span>
                          )}
                        </div>
                        {gstEnrichment?.gstStatus && gstEnrichment.gstStatus !== 'Active' && (
                          <p className="mt-1 flex items-center gap-1 text-xs font-medium text-red-600">
                            <AlertCircle size={12} />
                            This vendor's GST is {gstEnrichment.gstStatus}. It cannot be added — Purchase Orders and GRNs would be blocked.
                          </p>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => { setHasGst(null); setForm(prev => ({ ...prev, gstin: '', pan: '', stateCode: '' })); setGstFetched(false); }}
                      className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Go back
                    </button>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Vendor Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] ${gstSource === 'api' && form.name ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}
                    />
                    {gstEnrichment && gstVendorName(gstEnrichment).legalNameLine && (
                      <p className="mt-1 text-xs text-gray-500">Legal name: {gstVendorName(gstEnrichment).legalNameLine}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Contact Person</label>
                    <input type="text" value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                      <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">PAN</label>
                      <input type="text" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                        className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] ${gstFetched ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">State Code</label>
                      <input type="text" value={form.stateCode} onChange={(e) => setForm({ ...form, stateCode: e.target.value })}
                        className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] ${gstFetched ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Payment Terms</label>
                      <select value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                        {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Billing Address */}
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="mb-3 text-sm font-semibold text-gray-800">Billing Address</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Address Line 1</label>
                        <input type="text" value={form.billingAddress.line1}
                          onChange={(e) => { const ba = { ...form.billingAddress, line1: e.target.value }; setForm({ ...form, billingAddress: ba, ...(form.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                          className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] ${gstSource === 'api' && form.billingAddress.line1 ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">City</label>
                          <input type="text" value={form.billingAddress.city}
                            onChange={(e) => { const ba = { ...form.billingAddress, city: e.target.value }; setForm({ ...form, billingAddress: ba, ...(form.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">State</label>
                          <input type="text" value={form.billingAddress.state}
                            onChange={(e) => { const ba = { ...form.billingAddress, state: e.target.value }; setForm({ ...form, billingAddress: ba, ...(form.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                            className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)] ${gstFetched && form.billingAddress.state ? 'border-green-300 bg-green-50' : 'border-gray-300'}`} />
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Pincode</label>
                          <input type="text" value={form.billingAddress.pincode}
                            onChange={(e) => { const ba = { ...form.billingAddress, pincode: e.target.value }; setForm({ ...form, billingAddress: ba, ...(form.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                            placeholder="110001"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Address */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-800">Delivery Address</h3>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={form.sameAsB}
                          onChange={(e) => setForm({ ...form, sameAsB: e.target.checked, ...(e.target.checked ? { deliveryAddress: { ...form.billingAddress } } : {}) })}
                          className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                        Same as billing address
                      </label>
                    </div>
                    {!form.sameAsB && (
                      <div className="space-y-3">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Address Line 1</label>
                          <input type="text" value={form.deliveryAddress.line1}
                            onChange={(e) => setForm({ ...form, deliveryAddress: { ...form.deliveryAddress, line1: e.target.value } })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">City</label>
                            <input type="text" value={form.deliveryAddress.city}
                              onChange={(e) => setForm({ ...form, deliveryAddress: { ...form.deliveryAddress, city: e.target.value } })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">State</label>
                            <input type="text" value={form.deliveryAddress.state}
                              onChange={(e) => setForm({ ...form, deliveryAddress: { ...form.deliveryAddress, state: e.target.value } })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Pincode</label>
                            <input type="text" value={form.deliveryAddress.pincode}
                              onChange={(e) => setForm({ ...form, deliveryAddress: { ...form.deliveryAddress, pincode: e.target.value } })}
                              placeholder="110001"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Step 2b: No GST — minimal form */}
              {hasGst === false && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Vendor Name *</label>
                    <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" autoFocus />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Contact Person</label>
                    <input type="text" value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                      <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Payment Terms</label>
                    <select value={form.paymentTerms} onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                      {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => setHasGst(null)}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Go back
                  </button>
                </>
              )}
            </div>
            {hasGst !== null && (
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <RequestCloseButton
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </RequestCloseButton>
                <button
                  onClick={handleCreate}
                  disabled={saving || !form.name || Boolean(gstEnrichment?.gstStatus && gstEnrichment.gstStatus !== 'Active')}
                  className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  Add Vendor
                </button>
              </div>
            )}
        </Modal>
      )}

      {/* Edit Vendor Modal */}
      {showEditModal && (
        <Modal open onClose={() => setShowEditModal(false)}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Vendor</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Vendor Name *</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                {(() => {
                  const line = editEnrichment ? gstVendorName(editEnrichment).legalNameLine : legalNameUnder(editForm.name, editSavedLegal);
                  return line ? <p className="mt-1 text-xs text-gray-500">Legal name: {line}</p> : null;
                })()}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Contact Person</label>
                <input type="text" value={editForm.contactPerson} onChange={e => setEditForm({ ...editForm, contactPerson: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
                  <input type="text" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">GSTIN</label>
                  <input type="text" value={editForm.gstin} onChange={e => handleEditGstinChange(e.target.value)} placeholder="22AAAAA0000A1Z5"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  {editGstFetching && <p className="mt-1 text-xs text-gray-500">Checking GST…</p>}
                  {!editGstFetching && editGstSource === 'api' && <p className="mt-1 text-xs text-green-600">Details fetched — review and edit below if needed</p>}
                  {editGstError && <p className="mt-1 text-xs text-orange-600">{editGstError}</p>}
                  {!editGstFetching && editForm.gstin.length === 15 && (
                    <button type="button" onClick={() => handleEditGstinChange(editForm.gstin, true)}
                      className="mt-1 text-xs text-[var(--saffron-600)] underline hover:opacity-80">
                      Fill name and address from GST
                    </button>
                  )}
                  {editEnrichment?.gstStatus && <p className={`mt-1 text-xs font-medium ${editEnrichment.gstStatus === 'Active' ? 'text-green-700' : 'text-red-600'}`}>GST: {editEnrichment.gstStatus}</p>}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">PAN</label>
                  <input type="text" value={editForm.pan} onChange={e => setEditForm({ ...editForm, pan: e.target.value })} placeholder="ABCDE1234F"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">State Code</label>
                  <input type="text" value={editForm.stateCode} onChange={e => setEditForm({ ...editForm, stateCode: e.target.value })} placeholder="06"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Payment Terms</label>
                  <select value={editForm.paymentTerms} onChange={e => setEditForm({ ...editForm, paymentTerms: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Billing Address */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-800">Billing Address</h3>
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Address Line 1</label>
                    <input type="text" value={editForm.billingAddress.line1}
                      onChange={(e) => { const ba = { ...editForm.billingAddress, line1: e.target.value }; setEditForm({ ...editForm, billingAddress: ba, ...(editForm.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">City</label>
                      <input type="text" value={editForm.billingAddress.city}
                        onChange={(e) => { const ba = { ...editForm.billingAddress, city: e.target.value }; setEditForm({ ...editForm, billingAddress: ba, ...(editForm.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">State</label>
                      <input type="text" value={editForm.billingAddress.state}
                        onChange={(e) => { const ba = { ...editForm.billingAddress, state: e.target.value }; setEditForm({ ...editForm, billingAddress: ba, ...(editForm.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Pincode</label>
                      <input type="text" value={editForm.billingAddress.pincode}
                        onChange={(e) => { const ba = { ...editForm.billingAddress, pincode: e.target.value }; setEditForm({ ...editForm, billingAddress: ba, ...(editForm.sameAsB ? { deliveryAddress: ba } : {}) }); }}
                        placeholder="110001"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="border-t border-gray-200 pt-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-800">Delivery Address</h3>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={editForm.sameAsB}
                      onChange={(e) => setEditForm({ ...editForm, sameAsB: e.target.checked, ...(e.target.checked ? { deliveryAddress: { ...editForm.billingAddress } } : {}) })}
                      className="h-4 w-4 rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                    Same as billing address
                  </label>
                </div>
                {!editForm.sameAsB && (
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Address Line 1</label>
                      <input type="text" value={editForm.deliveryAddress.line1}
                        onChange={(e) => setEditForm({ ...editForm, deliveryAddress: { ...editForm.deliveryAddress, line1: e.target.value } })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">City</label>
                        <input type="text" value={editForm.deliveryAddress.city}
                          onChange={(e) => setEditForm({ ...editForm, deliveryAddress: { ...editForm.deliveryAddress, city: e.target.value } })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">State</label>
                        <input type="text" value={editForm.deliveryAddress.state}
                          onChange={(e) => setEditForm({ ...editForm, deliveryAddress: { ...editForm.deliveryAddress, state: e.target.value } })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Pincode</label>
                        <input type="text" value={editForm.deliveryAddress.pincode}
                          onChange={(e) => setEditForm({ ...editForm, deliveryAddress: { ...editForm.deliveryAddress, pincode: e.target.value } })}
                          placeholder="110001"
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleEdit} disabled={editSaving || editGstFetching || !editForm.name || Boolean(editEnrichment?.gstStatus && editEnrichment.gstStatus !== 'Active')}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
